// # 화면이 보는 상태
//
// ## 서버 상태와 UI 상태를 섞지 않는다
// 메시지 목록은 **서버가 진실**이고, 사이드바 접힘은 **로컬이 진실**이다.
// 이 둘을 한 저장소에 섞으면 "새로고침하면 왜 초기화되지?" 와
// "왜 다른 기기에 반영이 안 되지?" 가 동시에 생긴다.
//
// 여기 담는 것은 **동기화 엔진이 밀어 넣는 것**뿐이다. 조회는 TanStack Query 가 한다.

import type { ChannelEvent, ChannelId, Event, Seq, UserId } from '@maru/protocol'
import { has_channel, unread as unread_calc } from '@maru/protocol'
import { create } from 'zustand'

export interface ChannelSummary {
  last: Seq
  read_upto: Seq
  mention_count: number
}

export interface ChatState {
  conn: 'Dropped' | 'Connecting' | 'Attached' | 'catching_up'
  channels: Map<ChannelId, ChannelSummary>
  /** 채널별로 지금 타이핑 중인 사람들. 타이머로 스스로 사라진다. */
  typing: Map<ChannelId, Set<UserId>>

  conn_changed(next: ChatState['conn']): void
  /**
   * 시작할 때 REST 로 받은 것을 통째로 심는다. 소켓이 오기 전에 화면이 "0" 이 아니라
   * 진짜 숫자를 보여야 하고, 엔진의 커서도 여기서 나온다.
   */
  seed_channel(
    channels: Iterable<{ channel: ChannelId; last: Seq; read_upto: Seq; mention_count: number }>,
  ): void
  on_event(e: Event): void
  /** 새로 열었거나 초대받은 채널 하나. 이미 알면 건드리지 않는다. */
  add_channel(channel: ChannelId, last: Seq): void
  drop_channel(channel: ChannelId): void
  read_bump(channel: ChannelId, upto: Seq): void
  /** 커서를 그 자리에 **정확히** 놓는다. 뒤로도 간다 — 사람이 되돌린 것이다. */
  read_set(channel: ChannelId, upto: Seq): void
  /** 든 채널을 모두 끝까지 읽은 것으로. */
  read_all(): void
  unread(channel: ChannelId): number
  badge_total(): number
}

/** 타이핑 표시가 저절로 사라지는 시간. 서버가 "멈췄다" 를 보내 주지 않는다. */
const TYPING_TTL_MS = 4000

const NOTHING_YET: ChannelSummary = { last: 0 as Seq, read_upto: 0 as Seq, mention_count: 0 }

/** 사건 하나가 채널 요약을 어떻게 바꾸는가. 바꿀 게 없으면 **같은 객체**를 돌려준다. */
function summary_after(
  now: ChannelSummary,
  e: Exclude<ChannelEvent, { t: 'typing' }>,
): ChannelSummary {
  switch (e.t) {
    case 'message':
      return { ...now, last: e.seq }
    case 'read_state':
      // 다른 기기에서 읽었다. **뒤로 가지 않는다** — 늦게 도착한 오래된 사건일 수 있다.
      return {
        ...now,
        read_upto: (e.upto > now.read_upto ? e.upto : now.read_upto) as Seq,
        mention_count: e.upto >= now.last ? 0 : now.mention_count,
      }
    case 'mentioned':
      // 배지는 **읽지 않은 호출**만 센다. 이미 읽은 자리 뒤의 호출이어야 오른다.
      return e.seq > now.read_upto ? { ...now, mention_count: now.mention_count + 1 } : now
    case 'edited':
    case 'deleted':
    case 'reacted':
    case 'membership':
    case 'thread_replied':
    case 'reaction_received':
    case 'kind_changed':
    case 'ephemeral':
    case 'view_opened':
    case 'huddle':
    case 'canvas_changed':
    case 'canvas_here':
    case 'channel_changed':
    case 'pins_changed':
    case 'bookmarks_changed':
    case 'draft_changed':
    case 'poll_changed':
      // 타임라인·반응·명단은 각자의 캐시가 든다. 여기서는 할 일이 없다.
      return now
  }
}

/**
 * 사람마다 걸린 "쓰는 중" 불 끄기 시계. 새 표시가 오면 옛 시계를 거둔다 — 거두지 않으면 앞선 표시의
 * 시계가 방금 이어진 불을 꺼서, 계속 치는 사람의 불이 3초 치고 1초 꺼지며 깜빡였다(3차 F170).
 */
const typing_clocks = new Map<string, ReturnType<typeof setTimeout>>()

function typing_started(
  typing: Map<ChannelId, Set<UserId>>,
  channel: ChannelId,
  who: UserId,
  later: () => void,
): void {
  const people = new Set(typing.get(channel) ?? [])
  people.add(who)
  typing.set(channel, people)
  const key = `${channel}:${who}`
  clearTimeout(typing_clocks.get(key))
  typing_clocks.set(
    key,
    setTimeout(() => {
      typing_clocks.delete(key)
      later()
    }, TYPING_TTL_MS),
  )
}

/** 그 사람이 글을 보냈으면 더는 쓰는 중이 아니다. 불이 남아 있으면 끄고, 아니면 그대로 돌려준다. */
function typing_done(
  typing: Map<ChannelId, Set<UserId>>,
  channel: ChannelId,
  who: UserId,
): Map<ChannelId, Set<UserId>> {
  if (typing.get(channel)?.has(who) !== true) return typing
  const key = `${channel}:${who}`
  clearTimeout(typing_clocks.get(key))
  typing_clocks.delete(key)
  const next = new Map(typing)
  const people = new Set(next.get(channel))
  people.delete(who)
  next.set(channel, people)
  return next
}

function forget_typing(
  get: () => ChatState,
  set: (part: Partial<ChatState>) => void,
  channel: ChannelId,
  who: UserId,
): void {
  const current = new Map(get().typing)
  const line = new Set(current.get(channel) ?? [])
  line.delete(who)
  current.set(channel, line)
  set({ typing: current })
}

export const chat_store = create<ChatState>((set, get) => ({
  conn: 'Dropped',
  channels: new Map(),
  typing: new Map(),

  conn_changed: (next) => set({ conn: next }),

  seed_channel: (channels) =>
    set(() => {
      const next = new Map<ChannelId, ChannelSummary>()
      for (const c of channels)
        next.set(c.channel, {
          last: c.last,
          read_upto: c.read_upto,
          mention_count: c.mention_count,
        })
      return { channels: next }
    }),

  on_event: (e) =>
    set((before) => {
      // 테넌트 전체 사건(이모지·그룹·사람)은 채널 요약과 무관하다 — 조회 캐시가 맡는다(`stale_queries`).
      if (!has_channel(e)) return before
      if (e.t === 'typing') {
        const typing = new Map(before.typing)
        typing_started(typing, e.channel, e.who, () => forget_typing(get, set, e.channel, e.who))
        return { typing }
      }
      // 글이 도착한 순간 "쓰는 중" 을 끈다. 시계(4초)를 기다리면 방금 보낸 글 밑에 불이 남아 있다(3차 F170).
      const typing =
        e.t === 'message' ? typing_done(before.typing, e.channel, e.author) : before.typing
      const now = before.channels.get(e.channel) ?? NOTHING_YET
      const next = summary_after(now, e)
      if (next === now) return typing === before.typing ? before : { typing }
      const channels = new Map(before.channels)
      channels.set(e.channel, next)
      return typing === before.typing ? { channels } : { channels, typing }
    }),

  add_channel: (channel, last) =>
    set((before) => {
      if (before.channels.has(channel)) return before
      const channels = new Map(before.channels)
      channels.set(channel, { last, read_upto: last, mention_count: 0 })
      return { channels }
    }),

  drop_channel: (channel) =>
    set((before) => {
      if (!before.channels.has(channel)) return before
      const channels = new Map(before.channels)
      channels.delete(channel)
      return { channels }
    }),

  read_bump: (Channel, upto) =>
    set((before) => {
      const channels = new Map(before.channels)
      const now = channels.get(Channel)
      if (!now || upto <= now.read_upto) return before
      channels.set(Channel, {
        ...now,
        read_upto: upto,
        mention_count: upto >= now.last ? 0 : now.mention_count,
      })
      return { channels }
    }),

  read_set: (channel, upto) =>
    set((before) => {
      const now = before.channels.get(channel)
      if (!now) return before
      const channels = new Map(before.channels)
      channels.set(channel, { ...now, read_upto: upto })
      return { channels }
    }),

  read_all: () =>
    set((before) => {
      const channels = new Map<ChannelId, ChannelSummary>()
      for (const [id, c] of before.channels) {
        channels.set(id, { ...c, read_upto: c.last, mention_count: 0 })
      }
      return { channels }
    }),

  unread: (Channel) => {
    const c = get().channels.get(Channel)
    return c ? unread_calc(c.last, c.read_upto) : 0
  },

  badge_total: () => {
    // 배지는 **멘션 수**다. 안읽음 총합이 아니다.
    // 안읽음을 배지로 쓰면 숫자가 늘 세 자리라 아무 의미가 없어진다.
    let sum = 0
    for (const c of get().channels.values()) sum += c.mention_count
    return sum
  },
}))
